'use client';

import { useState } from 'react';
import { Check, Copy, ExternalLink, MapPin, Star } from 'lucide-react';

const extractPlaceId = (value: string) => {
  const input = value.trim();
  if (/^[A-Za-z0-9_-]{20,}$/.test(input)) return input;

  let url: URL;
  try {
    url = new URL(input.includes('://') ? input : `https://${input}`);
  } catch {
    return null;
  }

  const hostname = url.hostname.toLowerCase();
  if (
    !(
      hostname === 'google.com' ||
      hostname.endsWith('.google.com') ||
      hostname === 'maps.app.goo.gl' ||
      hostname === 'goo.gl' ||
      hostname === 'g.page'
    )
  ) {
    return null;
  }

  const directPlaceId = ['placeid', 'place_id', 'query_place_id', 'query_placeid']
    .map((key) => url.searchParams.get(key))
    .find(Boolean);
  if (directPlaceId) return directPlaceId;

  const query = `${url.searchParams.get('q') || ''} ${url.searchParams.get('query') || ''}`;
  const queryMatch = query.match(/place_id\s*[:=]\s*([A-Za-z0-9_-]{20,})/i);
  if (queryMatch) return queryMatch[1];

  const pathMatch = decodeURIComponent(url.href).match(/!1s([A-Za-z0-9_-]{20,})/);
  return pathMatch?.[1] || null;
};

const GoogleReviewLinkGenerator = () => {
  const [input, setInput] = useState('');
  const [reviewLink, setReviewLink] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const generateLink = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setReviewLink('');
    setCopied(false);

    const value = input.trim();
    if (!value) {
      setError('Enter a Google Maps URL or Place ID to get started.');
      return;
    }

    try {
      const url = new URL(value.includes('://') ? value : `https://${value}`);
      if (
        url.hostname.toLowerCase() === 'search.google.com' &&
        url.pathname.includes('/local/writereview') &&
        url.searchParams.has('placeid')
      ) {
        setReviewLink(url.toString());
        return;
      }
      if (url.hostname.toLowerCase() === 'g.page' && /\/review\/?$/.test(url.pathname)) {
        setReviewLink(url.toString());
        return;
      }
    } catch {
      // A plain Place ID is valid input too; extraction below handles it.
    }

    const placeId = extractPlaceId(value);
    if (!placeId) {
      setError(
        'We could not find a Place ID in that input. Paste a Google Maps URL that includes one, or paste the Place ID directly.'
      );
      return;
    }

    setReviewLink(`https://search.google.com/local/writereview?placeid=${encodeURIComponent(placeId)}`);
  };

  const copyLink = async () => {
    if (!reviewLink) return;
    try {
      await navigator.clipboard.writeText(reviewLink);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Clipboard access was blocked. Select the generated link and copy it manually.');
    }
  };

  const mapsSearchUrl = input.trim()
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(input.trim())}`
    : 'https://www.google.com/maps';

  return (
    <main className="google-review-generator min-h-[70vh] bg-[#F6F8FC] px-4 py-12 schibsted-grotesk text-[#111740] sm:py-16">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 max-w-3xl">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#D9E2FA] bg-white px-3 py-1.5 text-[13px] font-medium text-[#195AFF]">
            <Star className="h-4 w-4 fill-[#195AFF]" />
            Free tool · No sign-up
          </div>
          <h1 className="text-[32px] font-semibold leading-tight text-[#111740] sm:text-[42px]">
            Google Review Link Generator
          </h1>
          <p className="mt-3 max-w-2xl text-[16px] leading-7 text-[#59647E]">
            Make it easy for customers to leave a review. Turn your Google Place ID or Maps link into a direct link to your review form.
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-[minmax(0,1.25fr)_minmax(280px,0.75fr)] lg:items-start">
          <section className="rounded-[12px] border border-[#E0E5EF] bg-white p-5 shadow-[0_12px_35px_rgba(17,23,64,0.06)] sm:p-7">
            <form onSubmit={generateLink}>
              <label htmlFor="google-maps-input" className="mb-2 block text-[15px] font-semibold text-[#111740]">
                Google Maps link or Place ID
              </label>
              <div className="relative">
                <MapPin className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#7B86A0]" />
                <input
                  id="google-maps-input"
                  type="text"
                  value={input}
                  onChange={(event) => {
                    setInput(event.target.value);
                    setError('');
                    setReviewLink('');
                  }}
                  placeholder="Paste a Google Maps URL or Place ID"
                  autoComplete="url"
                  className="h-14 w-full rounded-[8px] border border-[#D6DCEA] bg-white pl-12 pr-4 text-[15px] text-[#111740] outline-none transition placeholder:text-[#8992A8] focus:border-[#195AFF] focus:ring-4 focus:ring-[#195AFF]/10"
                  aria-describedby={error ? 'review-link-error' : 'maps-input-help'}
                  aria-invalid={Boolean(error)}
                />
              </div>
              <p id="maps-input-help" className="mt-2 text-[13px] leading-5 text-[#68738D]">
                Use a Place ID or a Google Maps URL containing a Place ID. Business-name lookup needs a Google Places API key.
              </p>

              {error && (
                <p id="review-link-error" role="alert" className="mt-3 text-[13px] leading-5 text-[#B42318]">
                  {error}{' '}
                  <a href={mapsSearchUrl} target="_blank" rel="noreferrer" className="font-semibold text-[#195AFF] underline underline-offset-2">
                    Find the business on Google Maps
                  </a>
                </p>
              )}

              <button
                type="submit"
                className="mt-5 inline-flex h-12 w-full items-center justify-center gap-2 rounded-[8px] bg-[#195AFF] px-5 text-[15px] font-semibold text-white transition hover:bg-[#1249D8] focus:outline-none focus:ring-4 focus:ring-[#195AFF]/20 sm:w-auto"
              >
                Generate review link
                <ExternalLink className="h-4 w-4" />
              </button>
            </form>

            {reviewLink && (
              <div className="mt-6 border-t border-[#E7EAF1] pt-5" aria-live="polite">
                <label htmlFor="generated-review-link" className="mb-2 block text-[14px] font-semibold text-[#111740]">
                  Your Google review link
                </label>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <input
                    id="generated-review-link"
                    readOnly
                    value={reviewLink}
                    onFocus={(event) => event.currentTarget.select()}
                    className="h-11 min-w-0 flex-1 rounded-[8px] border border-[#D6DCEA] bg-[#F8FAFF] px-3 text-[13px] text-[#36415D] outline-none"
                  />
                  <button
                    type="button"
                    onClick={copyLink}
                    className="inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-[8px] border border-[#195AFF] px-4 text-[14px] font-semibold text-[#195AFF] transition hover:bg-[#F2F6FF] focus:outline-none focus:ring-4 focus:ring-[#195AFF]/15"
                  >
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    {copied ? 'Copied' : 'Copy link'}
                  </button>
                  <a
                    href={reviewLink}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Open generated Google review link"
                    className="inline-flex h-11 w-11 items-center justify-center self-end rounded-[8px] border border-[#D6DCEA] text-[#4A5578] transition hover:border-[#195AFF] hover:text-[#195AFF] focus:outline-none focus:ring-4 focus:ring-[#195AFF]/15 sm:self-auto"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              </div>
            )}
          </section>

          <aside className="border-l-2 border-[#DCE6FF] py-1 pl-5 sm:pl-6">
            <h2 className="text-[18px] font-semibold text-[#111740]">How to create your link</h2>
            <ol className="mt-5 space-y-5">
              {[
                'Open your business listing on Google Maps.',
                'Copy a Maps URL with its Place ID, or find the Place ID in your Google Business Profile tools.',
                'Paste it here, generate your link, then share it with customers.',
              ].map((step, index) => (
                <li key={step} className="flex gap-3 text-[14px] leading-6 text-[#59647E]">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#E8EEFF] text-[13px] font-semibold text-[#195AFF]">
                    {index + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
            <a
              href="https://www.google.com/maps"
              target="_blank"
              rel="noreferrer"
              className="mt-6 inline-flex items-center gap-2 text-[14px] font-semibold text-[#195AFF] hover:underline underline-offset-4"
            >
              Open Google Maps <ExternalLink className="h-4 w-4" />
            </a>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default GoogleReviewLinkGenerator;